import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { StatusBadge, PriorityBadge } from '../components/StatusBadge';
import { formatDate } from '../utils/dateUtils';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Search, Inbox } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InformedRequestsPage() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/tickets/informed`, { headers: getAuthHeader() });
      if (res.ok) setTickets(await res.json());
    } catch (e) {
      console.error('Fetch informed requests error:', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const filtered = tickets.filter(t => {
    const q = search.toLowerCase();
    return (
      (t.ticket_id || '').toLowerCase().includes(q) ||
      (t.request_type || '').toLowerCase().includes(q) ||
      (t.requester_name || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="informed-requests-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Informed Requests</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Requests where your department was added as a SPOC to be informed. You can view details, track status and comment — you are not the assignee.
          </p>
        </div>

        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search requests..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="informed-search-input"
          />
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground empty-state">
                <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No informed requests</p>
                <p className="text-sm mt-1">When a Guest Arrival or Client Factory Visit request informs your department, it will appear here.</p>
              </div>
            ) : (
              <div className="rounded-md border-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Request ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="hidden md:table-cell">Raised By</TableHead>
                      <TableHead className="hidden sm:table-cell">Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((t) => (
                      <TableRow
                        key={t.ticket_id}
                        className="table-row-hover cursor-pointer"
                        onClick={() => navigate(`/tickets/${t.ticket_id}`)}
                        data-testid={`informed-row-${t.ticket_id}`}
                      >
                        <TableCell><span className="ticket-id-link font-mono text-sm">{t.ticket_id}</span></TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate">
                          {t.request_type === 'Other' ? t.other_request_type : t.request_type}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground">{t.requester_name}</TableCell>
                        <TableCell className="hidden sm:table-cell"><PriorityBadge priority={t.priority} /></TableCell>
                        <TableCell><StatusBadge status={t.status} /></TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground text-sm">{formatDate(t.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
